import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';

// Common Pages
import NotFound from './pages/NotFound';
import Developers from './components/common/Developers';

// Student Pages
import StudentDashboard from './pages/student/StudentDashboard';
import Login from './pages/student/Login';
import CompleteRegistration from './pages/student/CompleteRegistration';
import PreferencesSubmission from './pages/student/PreferencesSubmission';
import MySchedule from './components/student/MySchedule';

// Admin Pages
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminLogin from './pages/admin/AdminLogin';
import ManageCompanies from './pages/admin/ManageCompanies';
import ManageStudents from './pages/admin/ManageStudents';
import SchedulingDashboard from './pages/admin/SchedulingDashboard';
import GlobalSchedulingPage from './pages/admin/GlobalSchedulingPage';
import CustomScheduling from './pages/admin/CustomScheduling';
import GDScheduling from './pages/admin/GDScheduling';

// Protected Route Component
const ProtectedRoute = ({ element, allowAdmin, allowStudent }) => {
  const { isAuthenticated, isAdmin, loading, user } = useAuth();

  console.log('Protected Route - Auth Details:', {
    isAuthenticated,
    isAdmin,
    loading,
    user,
    allowAdmin,
    allowStudent,
  });

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/" />;
  }

  // Admin-specific routing
  if (isAdmin && !allowAdmin) {
    console.log('Redirecting admin to admin dashboard');
    return <Navigate to="/admin/dashboard" />;
  }

  // Student-specific routing
  if (!isAdmin && !allowStudent) {
    console.log('Redirecting student to student dashboard');
    return <Navigate to="/dashboard" />;
  }

  // For students
  if (!isAdmin && allowStudent) {
    // Redirect to complete registration if not complete
    if (!user.is_registration_complete && window.location.pathname !== '/complete-registration') {
      console.log('Student registration incomplete, redirecting');
      return <Navigate to="/complete-registration" />;
    }

    // Check portal access for students
    if (!user.can_access_portal) {
      console.log('Student lacks portal access');
      return (
        <div className="container mt-5">
          <div className="alert alert-warning">
            <h4>Access Restricted</h4>
            <p>
              Your account doesn&apos;t have access to the portal yet. Please check back later or contact the administrator.
            </p>
          </div>
        </div>
      );
    }
  }

  return element;
};

const AppRoutes = () => {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<Login />} />
      <Route path="/admin/login" element={<AdminLogin />} />

      {/* Student Protected Routes */}
      <Route
        path="/dashboard"
        element={<ProtectedRoute element={<StudentDashboard />} allowStudent={true} allowAdmin={false} />}
      />
      <Route
        path="/complete-registration"
        element={<ProtectedRoute element={<CompleteRegistration />} allowStudent={true} allowAdmin={false} />}
      />
      <Route
        path="/preferences"
        element={<ProtectedRoute element={<PreferencesSubmission />} allowStudent={true} allowAdmin={false} />}
      />
      <Route
        path="/my-interview"
        element={<ProtectedRoute element={<MySchedule />} allowStudent={true} allowAdmin={false} />}
      />
      <Route
        path="/developers"
        element={<ProtectedRoute element={<Developers />} allowStudent={true} allowAdmin={true} />}
      />

      {/* Admin Protected Routes */}
      <Route
        path="/admin/dashboard"
        element={<ProtectedRoute element={<AdminDashboard />} allowStudent={false} allowAdmin={true} />}
      />
      <Route
        path="/admin/companies"
        element={<ProtectedRoute element={<ManageCompanies />} allowStudent={false} allowAdmin={true} />}
      />
      <Route
        path="/admin/students"
        element={<ProtectedRoute element={<ManageStudents />} allowStudent={false} allowAdmin={true} />}
      />
      <Route
        path="/admin/scheduling"
        element={<ProtectedRoute element={<SchedulingDashboard />} allowStudent={false} allowAdmin={true} />}
      />
      <Route
        path="/admin/global-scheduling"
        element={<ProtectedRoute element={<GlobalSchedulingPage />} allowStudent={false} allowAdmin={true} />}
      />
      <Route
        path="/admin/custom-scheduling"
        element={<ProtectedRoute element={<CustomScheduling />} allowStudent={false} allowAdmin={true} />}
      />
      <Route
        path="/admin/gd-scheduling"
        element={<ProtectedRoute element={<GDScheduling />} allowStudent={false} allowAdmin={true} />}
      />

      {/* 404 Not Found */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

export default AppRoutes;